"use client";

import Image from "next/image";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { Check } from "lucide-react";
import {
  AnimatePresence,
  m,
  useInView,
  useReducedMotion,
  type Variants,
} from "motion/react";
import { useCountUp } from "@/lib/hooks/use-count-up";
import { useRevealOnce } from "@/lib/hooks/use-reveal-once";
import { useTranslation } from "@/lib/hooks/use-translation";
import {
  PANELS,
  PRICING_PANEL_EVENT,
  type PanelId,
  type PosPanelId,
} from "@/components/home/pricing-panel";
import {
  CRM_ADD_ONS,
  KITS,
  KIT_ADD_ONS,
  KIT_HARDWARE,
  PLAN_NAMES,
  SUBSCRIPTIONS,
  thb,
  yearly,
} from "@/lib/pricing/catalog";

/**
 * Send the reader to the contact form without leaving `#contact` behind in the
 * address bar.
 *
 * The CTAs stay real links — middle-click, ⌘-click and "copy link" all keep
 * working, and those paths deliberately fall through to the browser's own
 * fragment navigation. It is only the plain left click that scrolls by hand,
 * so the URL the reader ends up on (and might reload or share) is still the
 * top of the page rather than the form at the bottom of it.
 *
 * `scrollIntoView` with the default behaviour inherits `scroll-behavior:
 * smooth` from #scroll-container, and `scroll-mt-13` on the section still
 * clears the fixed header, so the trip looks identical to the anchor's.
 */
function scrollToContact(e: React.MouseEvent<HTMLAnchorElement>) {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
  const target = document.getElementById("contact");
  if (!target) return;
  e.preventDefault();
  target.scrollIntoView({ block: "start" });
}

type Feature = {
  label: string;
  /** Second line, set in semibold — the kits name the exact hardware model. */
  detail?: string;
  /** Add-on price, shown as a chip on the right; the row is then an option
      rather than something the headline price already covers. */
  addOn?: string;
  /** Rule above the row. The kits use it once, to separate what comes in the
      box from what can be added to it. */
  divided?: boolean;
};

type Plan = {
  id: string;
  name: string;
  blurb: string;
  price: string;
  /** Unit beside the price. Absent when the price is a call to sales rather
      than a number, which is why it isn't just part of `price`. */
  unit?: string;
  /** Line between the price and its note. Only Complete carries one, where
      "ปรับแต่งได้" needs a second line to say what it is scaled to. */
  priceTagline?: string;
  priceNote: string;
  image?: { src: string; alt: string };
  featuresTitle: string;
  features: Feature[];
  /** Frosted panel between the list and the button — the service promise the
      hardware kits carry. Left to wrap: the comp hard-breaks this line on the
      Starter card, but the featured card is ~18px narrower (its tray takes
      that), and the same break there spills the note onto a third line while
      its neighbours stay at two. Flowing, all three come out even. */
  note?: string;
  cta: string;
  /** Ribbon copy. Its presence is also what marks a plan as the featured one,
      since the comp never shows one without the other. */
  ribbon?: string;
};

/**
 * The plan data, built per render from the active locale rather than held as
 * module constants.
 *
 * Everything a translation can change — blurbs, feature lines, notes, CTAs —
 * comes out of the `pricing` namespace; what stays written here is the part
 * that does not translate: ids, prices, image paths and hardware model names.
 * Building them as functions of `t` is what lets the card renderers below keep
 * reading `plan.blurb` unchanged.
 */
type Translate = (key: string, options?: { returnObjects?: boolean }) => string;

const list = (t: Translate, key: string) =>
  (t(key, { returnObjects: true }) as unknown as string[]).map((label) => ({
    label,
  }));

/**
 * Which price the CRM cards quote. A year is twelve months at the same rate —
 * see `yearly` in the catalog — so this changes the figure and its unit, and
 * nothing else about a plan.
 */
export type Billing = "monthly" | "yearly";

/** Reservation, offered on every CRM plan rather than bundled into one. */
const crmAddOn = (t: Translate): Feature => ({
  label: t("pricing.crm.addOn"),
  addOn: `+${thb(CRM_ADD_ONS.reservation)}${t("pricing.crm.addOnUnit")}`,
  divided: true,
});

/** A per-branch subscription, priced for the billing cycle on screen. */
const crmPrice = (monthly: number, billing: Billing) =>
  thb(billing === "yearly" ? yearly(monthly) : monthly);

const crmUnit = (t: Translate, billing: Billing) =>
  t(billing === "yearly" ? "pricing.common.perBranchYearly" : "pricing.common.perBranchMonthly");

const crmPlans = (t: Translate, billing: Billing): Plan[] => [
  {
    id: "crm-starter",
    name: PLAN_NAMES.crmStarter,
    blurb: t("pricing.crm.starter.blurb"),
    price: crmPrice(SUBSCRIPTIONS.crmStarter, billing),
    unit: crmUnit(t, billing),
    priceNote: t("pricing.crm.starter.priceNote"),
    featuresTitle: t("pricing.common.featuresTitle"),
    features: [...list(t, "pricing.crm.starter.features"), crmAddOn(t)],
    cta: t("pricing.common.ctaStart"),
  },
  {
    id: "crm-pro",
    name: PLAN_NAMES.crmPro,
    blurb: t("pricing.crm.pro.blurb"),
    price: crmPrice(SUBSCRIPTIONS.crmPro, billing),
    unit: crmUnit(t, billing),
    priceNote: t("pricing.crm.pro.priceNote"),
    featuresTitle: t("pricing.common.featuresTitle"),
    features: [...list(t, "pricing.crm.pro.features"), crmAddOn(t)],
    cta: t("pricing.common.ctaTrial"),
    ribbon: t("pricing.crm.pro.ribbon"),
  },
  {
    id: "crm-complete",
    name: PLAN_NAMES.crmComplete,
    blurb: t("pricing.crm.complete.blurb"),
    // Scoped to the business rather than to a branch count, so it carries no
    // unit and the billing switch above leaves it alone.
    price: t("pricing.common.customPrice"),
    priceTagline: t("pricing.crm.complete.priceTagline"),
    priceNote: t("pricing.crm.complete.priceNote"),
    featuresTitle: t("pricing.common.featuresTitle"),
    features: [...list(t, "pricing.crm.complete.features"), crmAddOn(t)],
    cta: t("pricing.common.contactSales"),
  },
];

/** The three rows every kit shares, since only the model names differ. */
const kitExtras = (t: Translate): Feature[] => [
  { label: t("pricing.kitExtras.drawer"), addOn: `+${thb(KIT_ADD_ONS.drawer)}`, divided: true },
  { label: t("pricing.kitExtras.scanner"), addOn: `+${thb(KIT_ADD_ONS.scanner)}` },
  { label: t("pricing.kitExtras.adapter") },
];

const posKits = (t: Translate): Plan[] => [
  {
    id: "pos-starter",
    name: PLAN_NAMES.posStarterKit,
    blurb: t("pricing.kits.starter.blurb"),
    price: thb(KITS.starter),
    unit: t("pricing.common.baht"),
    priceNote: t("pricing.kits.oneOff"),
    image: {
      src: "/v2/pricing/pos-starter-kit.webp",
      alt: t("pricing.kits.starter.imageAlt"),
    },
    featuresTitle: t("pricing.common.kitFeaturesTitle"),
    features: [
      {
        label: t("pricing.kits.starter.tablet"),
        detail: KIT_HARDWARE.starter.tablet,
      },
      {
        label: t("pricing.kits.printer"),
        detail: KIT_HARDWARE.starter.printer,
      },
      ...kitExtras(t),
    ],
    note: t("pricing.kits.starter.note"),
    cta: t("pricing.common.ctaStart"),
  },
  {
    id: "pos-business",
    name: PLAN_NAMES.posBusinessKit,
    blurb: t("pricing.kits.business.blurb"),
    price: thb(KITS.business),
    unit: t("pricing.common.baht"),
    priceNote: t("pricing.kits.business.priceNote"),
    image: {
      src: "/v2/pricing/pos-business-kit.webp",
      alt: t("pricing.kits.business.imageAlt"),
    },
    featuresTitle: t("pricing.common.kitFeaturesTitle"),
    features: [
      {
        label: t("pricing.kits.business.tablet"),
        detail: KIT_HARDWARE.business.tablet,
      },
      {
        label: t("pricing.kits.printer"),
        detail: KIT_HARDWARE.business.printer,
      },
      ...kitExtras(t),
    ],
    note: t("pricing.kits.business.note"),
    cta: t("pricing.kits.business.cta"),
    ribbon: t("pricing.kits.business.ribbon"),
  },
  {
    id: "pos-enterprise",
    name: PLAN_NAMES.posEnterpriseKit,
    blurb: t("pricing.kits.enterprise.blurb"),
    price: thb(KITS.enterprise),
    unit: t("pricing.common.baht"),
    priceNote: t("pricing.kits.oneOff"),
    image: {
      src: "/v2/pricing/pos-enterprise-kit.webp",
      alt: t("pricing.kits.enterprise.imageAlt"),
    },
    featuresTitle: t("pricing.common.kitFeaturesTitle"),
    features: [
      {
        label: t("pricing.kits.enterprise.tablet"),
        detail: KIT_HARDWARE.enterprise.tablet,
      },
      { label: t("pricing.kits.printer"), detail: KIT_HARDWARE.enterprise.printer },
      ...kitExtras(t),
    ],
    note: t("pricing.kits.enterprise.note"),
    cta: t("pricing.common.ctaStart"),
  },
];

/** The monthly panel's nine capability tiles. */
const posIncluded = (t: Translate) =>
  t("pricing.monthly.included", { returnObjects: true }) as unknown as {
    title: string;
    body: string;
  }[];

/**
 * The two panels the reader is not looking at, as plain text.
 *
 * `AnimatePresence` below mounts exactly one panel at a time, which is right
 * for the animation and wrong for everything that reads the page without
 * running our JS: the HTML the server sends carried the CRM rail and nothing
 * else, so the monthly POS plan (฿590/branch, its nine capability tiles) and
 * all three hardware kits (฿13,900 / ฿21,900 / ฿25,900, their tablets,
 * printers and warranty notes) were invisible to every AI answer engine and to
 * any crawl that did not render — the POS prices being, of all the copy on
 * this page, the part people most often arrive searching for.
 *
 * Tabbed content held in the DOM and shown a panel at a time is a pattern
 * Google reads normally, and nothing here is hidden from a reader: whatever
 * this block holds is one tab away, and the moment that tab is selected the
 * real panel renders it and this block drops it, so no wording is ever
 * duplicated on screen.
 *
 * Text only, deliberately — no images, no `Price` counters, no Motion. The
 * point is the words; mounting the real panels hidden would pay their whole
 * hydration cost three times over for content nobody is looking at.
 */
function PanelMirror({ panel }: Readonly<{ panel: PanelId }>) {
  const { t } = useTranslation();

  const label: Record<PanelId, string> = {
    crm: "CRM Package",
    "pos-monthly": t("pricing.switch.posMonthly"),
    "pos-kit": t("pricing.switch.posKit"),
  };

  return (
    <div hidden>
      {PANELS.filter((id) => id !== panel).map((id) => (
        <div key={id}>
          <p>{label[id]}</p>
          <p>{t(`pricing.subheads.${id}`)}</p>
          {id !== "crm" ? <p>{t(`pricing.posCaptions.${id}`)}</p> : null}
          {id === "pos-monthly" ? (
            <MonthlyMirror />
          ) : (
            <PlanMirror plans={id === "crm" ? crmPlans(t, "monthly") : posKits(t)} />
          )}
        </div>
      ))}
    </div>
  );
}

/** `PlanRail`'s cards as text — same `Plan[]`, so the two cannot drift. */
function PlanMirror({ plans }: Readonly<{ plans: Plan[] }>) {
  return (
    <>
      {plans.map((plan) => (
        <div key={plan.id}>
          <p>{plan.name}</p>
          {plan.ribbon ? <p>{plan.ribbon}</p> : null}
          <p>{plan.blurb}</p>
          <p>
            {plan.price}
            {plan.unit ? ` ${plan.unit}` : ""}
            {plan.priceTagline ? ` ${plan.priceTagline}` : ""} — {plan.priceNote}
          </p>
          {plan.image ? <p>{plan.image.alt}</p> : null}
          <p>{plan.featuresTitle}</p>
          <ul>
            {plan.features.map((feature) => (
              <li key={feature.label}>
                {feature.label}
                {feature.detail ? ` — ${feature.detail}` : ""}
                {feature.addOn ? ` (${feature.addOn})` : ""}
              </li>
            ))}
          </ul>
          {plan.note ? <p>{plan.note}</p> : null}
          <p>{plan.cta}</p>
        </div>
      ))}
    </>
  );
}

/** `MonthlyPanel` as text. The two prices are the ones it strikes and shows. */
function MonthlyMirror() {
  const { t } = useTranslation();

  return (
    <div>
      <p>{t("pricing.monthly.ribbon")}</p>
      <p>{t("pricing.monthly.title")}</p>
      <p>{t("pricing.monthly.blurb")}</p>
      <p>
        {thb(SUBSCRIPTIONS.posMonthlyWas)} {thb(SUBSCRIPTIONS.posMonthly)}{" "}
        {t("pricing.monthly.unit")} — {t("pricing.monthly.unitNote")}
      </p>
      <p>{t("pricing.monthly.highlightTitle")}</p>
      <p>{t("pricing.monthly.highlightBody")}</p>
      <p>{t("pricing.monthly.includedTitle")}</p>
      <ul>
        {posIncluded(t).map((item) => (
          <li key={item.title}>
            {item.title} — {item.body}
          </li>
        ))}
      </ul>
      <p>{t("pricing.monthly.ctaNote")}</p>
    </div>
  );
}

/**
 * The three panels, in the order they sit on the rail (defined in
 * `pricing-panel.ts`, which the navbar also reads). Every switch moves the
 * reader along this line, so the slide direction is just the difference
 * between two indices — which is why the POS sub-tabs share the rail with the
 * CRM/POS switch instead of tracking a direction of their own.
 */

/** The site's standard ease-out; shared with the CSS in globals.css. */
const EASE = [0.22, 1, 0.36, 1] as const;

/** Its mirror, for the one motion that leaves rather than arrives. */
const EASE_IN = [0.55, 0, 1, 0.45] as const;

/** For a snapshot that can never change after the first read. */
const subscribeNever = () => () => {};

/** How far a card travels as it slides in or out. */
const SLIDE = 140;

/** Held before the first card moves, so the switch has settled under it. */
const CARD_DELAY = 0.12;

/** Gap between one card starting and the next, so the rail deals out left to right. */
const CARD_STAGGER = 0.22;

/**
 * The panel is only an orchestrator: it has nothing of its own to animate and
 * exists to hand `staggerChildren` down to the cards, which is what turns one
 * block sliding across into a rail being dealt out.
 */
const PANEL_VARIANTS: Variants = {
  enter: {},
  center: {
    transition: { delayChildren: CARD_DELAY, staggerChildren: CARD_STAGGER },
  },
  // Out in the order they came in, but tighter — a slow reversed stagger on
  // the way out just delays the panel the reader actually asked for.
  exit: { transition: { staggerChildren: 0.05 } },
};

/**
 * The card entrance: the pricing page on `main` reveals its plans with a
 * blur-and-scale (`.jd-blur-in` in globals.css — opacity, `scale(0.9)` and a
 * 10px blur on the site's ease-out), and these cards use the same figures so
 * the two pricing surfaces read as one thing. The horizontal travel is this
 * section's own, carrying the direction of the tab the reader pressed.
 */
function makeCardVariants(reduced: boolean): Variants {
  if (reduced) {
    // No blur, no travel, no scale — but still a fade, so a swapped panel
    // doesn't simply cut in.
    return {
      enter: { opacity: 0, filter: "blur(0px)", scale: 1, x: 0 },
      center: { opacity: 1, transition: { duration: 0.2 } },
      exit: { opacity: 0, transition: { duration: 0.1 } },
    };
  }

  return {
    enter: (dir: number) => ({
      opacity: 0,
      scale: 0.9,
      filter: "blur(10px)",
      x: dir * SLIDE,
    }),
    center: {
      opacity: 1,
      scale: 1,
      filter: "blur(0px)",
      x: 0,
      transition: { duration: 0.85, ease: EASE },
    },
    exit: (dir: number) => ({
      opacity: 0,
      scale: 0.96,
      filter: "blur(6px)",
      x: dir * -SLIDE,
      transition: { duration: 0.26, ease: EASE_IN },
    }),
  };
}

/** Gap between one feature row appearing and the next. */
const FEATURE_STAGGER = 0.1;

/**
 * The list is a second orchestrator nested inside the card's: the card is
 * already a stagger child of the panel, so its own children pick the cascade
 * up from where it starts and the rows run down the card as it arrives.
 * `delayChildren` holds them until the card is most of the way in, so the
 * rows are read against a card that has stopped moving.
 */
const FEATURE_LIST_VARIANTS: Variants = {
  enter: {},
  center: {
    transition: { delayChildren: 0.2, staggerChildren: FEATURE_STAGGER },
  },
  // No `exit`: on the way out the card carries its rows with it, and a second
  // cascade there would only hold the incoming panel up.
};

function makeFeatureVariants(reduced: boolean): Variants {
  if (reduced) {
    return {
      enter: { opacity: 0, y: 0 },
      center: { opacity: 1, transition: { duration: 0.15 } },
    };
  }

  return {
    enter: { opacity: 0, y: 8 },
    center: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: EASE },
    },
  };
}

/**
 * A price that races up to its figure as the card it sits in arrives — the
 * same `useCountUp` the tier mock-up in section 3 uses.
 *
 * The currency mark, the separators and any non-numeric price ("ติดต่อฝ่ายขาย")
 * are left alone: only the digits move. `tabular-nums` holds the glyph width
 * steady so the figure doesn't jitter sideways while it counts.
 */
function Price({
  value,
  armed,
  delay,
  className,
}: {
  value: string;
  /** Counting starts on the first render where this is true. */
  armed: boolean;
  /** Milliseconds after arming, matched to the card's own entrance. */
  delay: number;
  className?: string;
}) {
  const parts = value.match(/^(\D*)([\d,]+)(.*)$/);
  const target = parts ? Number(parts[2].replace(/,/g, "")) : 0;
  const count = useCountUp(target, { armed, duration: 900, delay });

  if (!parts) return <span className={className}>{value}</span>;

  return (
    <span className={className}>
      {parts[1]}
      <span className="tabular-nums">
        {/* Until the counter is armed this renders the price itself, not the
            zero the hook starts from: the card is still at `opacity: 0` so
            nobody sees the difference, but the server-rendered HTML — which is
            what a crawler or a reader without JS gets — then carries the real
            figure instead of ฿0. */}
        {armed ? Math.round(count).toLocaleString("en-US") : parts[2]}
      </span>
      {parts[3]}
    </span>
  );
}

function Tick() {
  return (
    <span
      aria-hidden
      className="grid size-6 shrink-0 place-items-center rounded-full bg-[#E8F3FF]"
    >
      <Check className="size-3 text-[#0084EB]" strokeWidth={2.5} />
    </span>
  );
}

/** One row of a plan's list: tick, copy, and an optional add-on price chip. */
function FeatureRow({
  feature,
  variants,
}: {
  feature: Feature;
  variants: Variants;
}) {
  return (
    <m.li
      variants={variants}
      className={`flex items-center gap-3 ${
        feature.divided ? "border-t border-[#D3D5DB] pt-4" : ""
      }`}
    >
      <Tick />
      <span className="flex-1 text-[14px]/[20px] text-black/80">
        {feature.label}
        {feature.detail ? (
          <>
            <br />
            <span className="font-semibold">{feature.detail}</span>
          </>
        ) : null}
      </span>
      {feature.addOn ? (
        // `text-primary-strong` rather than the comp's #2E99E6, which only
        // reaches 2.9:1 on this pale chip.
        <span className="shrink-0 rounded-full border border-[#C0E5FF] bg-[#EBF7FF] px-2 py-0.5 text-[14px]/[20px] font-semibold text-primary-strong">
          {feature.addOn}
        </span>
      ) : null}
    </m.li>
  );
}

/**
 * PlanCard — the shell both three-up rails use. The CRM plans and the hardware
 * kits differ only in whether they carry a photo and a service note, so they
 * share one card rather than two near-copies of it.
 *
 * The featured plan is that same card in a blue tray that shows above and
 * below it and carries the ribbon. From `lg` the tray is pulled out of the row
 * by 16px each way so it stands proud of its neighbours, as the comp shows,
 * without any of the three needing a fixed height.
 */
function PlanCard({
  plan,
  index,
  variants,
  featureVariants,
  armed,
}: {
  plan: Plan;
  /** Position in the rail, which sets when its price starts counting. */
  index: number;
  variants: Variants;
  /** Entrance for one feature row; the list orchestrates their order. */
  featureVariants: Variants;
  armed: boolean;
}) {
  const card = (
    <article
      className={`pr-card flex h-full flex-col bg-white p-6 ${
        plan.ribbon
          ? "rounded-[20px] border border-black/10"
          : "rounded-[18px] border border-[#E5E7EA]"
      }`}
    >
      <header>
        <h3 className="text-[24px]/[30px] font-bold text-black">{plan.name}</h3>
        {/* Three lines' worth of room whether the blurb fills it or not, so
            the prices below sit on one line across the rail — the cards can't
            share a subgrid, since the featured one is nested in its tray. */}
        <p className="text-[12px]/[18px] text-black/60 lg:min-h-[35px]">
          {plan.blurb}
        </p>
      </header>

      <div className="mt-6 border-b border-[#D3D5DB] pb-1.5">
        {/* Baseline-aligned rather than absolutely positioned as in the comp,
            so the unit still sits right however many digits the price runs
            to — and so it drops below on a narrow card instead of overlapping. */}
        <p className="flex flex-wrap items-baseline gap-x-1.5">
          <Price
            value={plan.price}
            armed={armed}
            delay={(CARD_DELAY + index * CARD_STAGGER) * 1000}
            className="text-[30px]/[36px] font-bold text-black"
          />
          {plan.unit ? (
            <span className="text-[12px]/[18px] text-black/60">
              {plan.unit}
            </span>
          ) : null}
        </p>
        {plan.priceTagline ? (
          <p className="text-[12px]/[18px] text-black/70">{plan.priceTagline}</p>
        ) : null}
        <p className="text-[10.5px]/[15px] text-black/60">{plan.priceNote}</p>
      </div>

      {plan.image ? (
        <Image
          src={plan.image.src}
          alt={plan.image.alt}
          width={624}
          height={578}
          sizes="(min-width: 1024px) 340px, (min-width: 640px) 50vw, 90vw"
          // A fixed box rather than the file's own ratio: the three kit photos
          // were cropped slightly differently, and letting each set its own
          // height knocked the lists under them 17px out of line.
          className="pr-shot mt-3 aspect-[27/25] w-full object-contain"
        />
      ) : null}

      <div className="mt-4">
        <h4 className="text-[13.5px]/[21px] font-semibold text-black">
          {plan.featuresTitle}
        </h4>
        <m.ul
          variants={FEATURE_LIST_VARIANTS}
          className="mt-3 flex flex-col gap-4"
        >
          {plan.features.map((feature) => (
            <FeatureRow
              key={feature.label}
              feature={feature}
              variants={featureVariants}
            />
          ))}
        </m.ul>
      </div>

      {plan.note ? (
        <p className="pr-note mt-4 px-5 py-4 text-[14px]/[20px] text-black/60">
          {plan.note}
        </p>
      ) : null}

      {/* `mt-auto` on the wrapper is what lands the three buttons on one line
          however unevenly the lists above them run. */}
      <div className="mt-auto pt-6">
        {/* An anchor, not a button: every plan CTA takes the reader to the
            contact form at the bottom of the page, the same jump the navbar
            and hero CTAs make. `block text-center` restores the centring a
            <button> gave for free. */}
        <a
          href="#contact"
          onClick={scrollToContact}
          data-track={`pricing_${plan.id}`}
          className={`pr-cta block w-full rounded-full px-2 py-3 text-center text-[16px]/[24px] font-semibold ${
            plan.ribbon
              ? "pr-cta-fill bg-primary-strong text-white"
              : "border border-[#9EA2AE] text-black/60"
          }`}
        >
          {plan.cta}
        </a>
      </div>
    </article>
  );

  if (!plan.ribbon)
    return (
      <m.li variants={variants} className="pr-plan flex">
        {card}
      </m.li>
    );

  return (
    <m.li
      variants={variants}
      className="pr-plan pr-plan-featured flex lg:-my-4"
    >
      <div className="flex w-full flex-col rounded-3xl bg-primary-strong px-[9px] pt-1.5 pb-[11px] shadow-[0_4px_4px_rgba(0,0,0,0.25)]">
        {/* Pale yellow on the deep brand blue reaches 4.6:1; the comp's
            #FFF2B0 lands at 4.45:1 and misses AA for 14px bold. */}
        <p className="pb-1.5 text-center text-[14px]/[20px] font-bold text-[#FFF6C8]">
          {plan.ribbon}
        </p>
        {card}
      </div>
    </m.li>
  );
}

/** The CRM tab and the hardware-kit tab: the same three-up rail. */
function PlanRail({
  plans,
  variants,
  featureVariants,
  armed,
}: {
  plans: Plan[];
  variants: Variants;
  featureVariants: Variants;
  armed: boolean;
}) {
  return (
    // The vertical padding is headroom, not spacing: the featured card hangs
    // 16px past the row on both sides and every card lifts on hover, and the
    // stage above clips (it has to, to hide the sliding panels).
    <ul className="mx-auto grid w-full max-w-[1140px] justify-items-center gap-8 lg:grid-cols-3 lg:items-stretch lg:gap-4 lg:py-6">
      {plans.map((plan, i) => (
        <PlanCard
          key={plan.id}
          plan={plan}
          index={i}
          variants={variants}
          featureVariants={featureVariants}
          armed={armed}
        />
      ))}
    </ul>
  );
}

/** The monthly POS tab: one wide card instead of a rail. */
function MonthlyPanel({
  variants,
  armed,
}: {
  variants: Variants;
  armed: boolean;
}) {
  const { t } = useTranslation();

  return (
    <m.div
      variants={variants}
      className="pr-plan mx-auto w-full max-w-[930px] overflow-hidden rounded-[32px] border border-[#E9E9E9] shadow-[0_4px_20.4px_0_rgba(0,0,0,0.10)]"
    >
      <p className="bg-primary-strong px-4 py-2 text-center text-[15px]/[24px] font-bold text-[#FFF6C8] sm:text-[16px]/[24px]">
        {t("pricing.monthly.ribbon")}
      </p>

      <div className="flex flex-col gap-5 bg-white p-5">
        <div className="flex flex-col gap-8 lg:flex-row lg:gap-6">
          <div className="flex flex-col justify-between gap-6 lg:w-[220px] lg:shrink-0">
            <div className="flex flex-col gap-2">
              <h3 className="text-[24px]/[30px] font-bold text-black">
                {t("pricing.monthly.title")}
              </h3>
              <p className="text-[14px]/[20px] text-black/60">
                {t("pricing.monthly.blurb")}
              </p>
            </div>

            <div className="flex flex-col gap-2 lg:px-3">
              <Price
                value={thb(SUBSCRIPTIONS.posMonthlyWas)}
                armed={armed}
                delay={CARD_DELAY * 1000}
                className="text-[24px]/[32px] font-semibold text-black/50 line-through"
              />
              <div className="rounded-2xl border border-[#DBEAFE] bg-[#FBFDFF] px-3.5 py-3">
                <Price
                  value={thb(SUBSCRIPTIONS.posMonthly)}
                  armed={armed}
                  delay={CARD_DELAY * 1000}
                  className="block text-[44px]/[52px] font-bold text-[#C0392B]"
                />
                <p className="text-[16px]/[24px] text-black/60">
                  {t("pricing.monthly.unit")}
                </p>
                <p className="mt-2 text-[14px]/[20px] text-black/60">
                  {t("pricing.monthly.unitNote")}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-6">
            <div className="pr-glass px-5 py-4">
              <p className="text-[20px]/[28px] font-semibold text-black">
                {t("pricing.monthly.highlightTitle")}
              </p>
              <p className="text-[14px]/[20px] text-black/60">
                {t("pricing.monthly.highlightBody")}
              </p>
            </div>

            <h4 className="text-[20px]/[28px] font-semibold text-black">
              {t("pricing.monthly.includedTitle")}
            </h4>
            <ul className="grid gap-x-3 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
              {posIncluded(t).map((item) => (
                <li key={item.title} className="flex items-start gap-4">
                  <Tick />
                  <span>
                    <span className="block text-[16px]/[24px] font-semibold text-black">
                      {item.title}
                    </span>
                    <span className="block text-[14px]/[20px] text-[#666666]">
                      {item.body}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <a
          href="#contact"
          onClick={scrollToContact}
          data-track="pricing_monthly"
          className="pr-cta pr-cta-fill block w-full rounded-full bg-primary-strong px-2 py-3 text-center text-[16px]/[24px] font-semibold text-white"
        >
          {t("pricing.monthly.ctaNote")}
        </a>
      </div>
    </m.div>
  );
}

/**
 * A segmented switch. The moving pill is one absolutely-positioned element
 * translated by the selected index, not a `layoutId` shared between the
 * options: the site runs `LazyMotion` with `domAnimation`, which leaves out
 * the layout-projection features `layoutId` depends on.
 *
 * `aria-pressed` toggles rather than tab roles, because two of these switches
 * drive the one region below and neither owns it — and because tab roles
 * promise arrow-key navigation that plain buttons don't provide.
 */
function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
  tone,
}: {
  label: string;
  options: { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
  /** `quiet` is the grey track with a white pill, `brand` the white track
      with a blue one. */
  tone: "quiet" | "brand";
}) {
  const index = Math.max(
    0,
    options.findIndex((option) => option.id === value),
  );

  return (
    <div
      role="group"
      aria-label={label}
      // `auto-cols-fr` is what the pill's width maths assumes: equal columns,
      // so `(100% - 4px) / n` is exactly one option. It also lets the track
      // size itself to the longest label from `sm` up, rather than being held
      // to the comp's pixel width and wrapping a Thai label onto two lines.
      className={`pr-switch relative grid w-full max-w-full grid-flow-col auto-cols-fr rounded-full border border-[#E6E6E6] p-0.5 sm:w-auto ${
        tone === "quiet" ? "bg-[#F7F7F7]" : "bg-white"
      }`}
      style={
        { "--pr-i": index, "--pr-n": options.length } as React.CSSProperties
      }
    >
      {/* Behind the labels, so the selected one keeps its own colour instead
          of being painted over by the pill. */}
      <span
        aria-hidden
        className={`pr-pill ${
          tone === "quiet" ? "border border-[#E6E6E6] bg-white" : "bg-primary-strong"
        }`}
      />
      {options.map((option) => {
        const selected = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(option.id)}
            className={`relative z-10 rounded-full px-4 py-1.5 text-[14px]/[20px] font-semibold transition-colors duration-300 select-none sm:whitespace-nowrap ${
              selected && tone === "brand" ? "text-white" : "text-black/60"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Pricing — v2 section 7: the CRM plans, the monthly POS plan and the POS
 * hardware kits, behind one switch.
 *
 * All three live on a single rail (see `PANELS`), so every switch — the
 * CRM/POS one and the POS sub-tabs alike — is a move left or right along it,
 * and the panel slides the way the reader travelled: pressing POS Packages
 * sends the CRM plans off to the left and brings POS in from the right, and
 * going back reverses it.
 *
 * Outgoing and incoming panels share one grid cell (`mode="sync"` plus
 * `[grid-area:1/1]`) so they cross instead of queueing. That leaves the frame
 * around them with no height of its own mid-swap, so it is measured off
 * whichever panel is current and transitioned to the next — otherwise
 * everything below would jump the instant the taller panel unmounted.
 */
export default function Pricing() {
  const { t } = useTranslation();
  const reveal = useRevealOnce<HTMLElement>(0.15);
  // The cards' entrance is a mount-time animation, and this section is a long
  // way below the fold — without a gate it would play to an empty screen and
  // be over before anyone scrolled here.
  //
  // Watching the rail rather than the section, which is what the header's own
  // CSS reveal watches: the heading, subtitle and switch stand ~250px above
  // the first card, so a section-level trigger fired while every card was
  // still under the fold, and the cascade was finished by the time the reader
  // had scrolled far enough to see one.
  const stageRef = useRef<HTMLDivElement>(null);
  const inView = useInView(stageRef, { once: true, margin: "-80px" });
  // `useRevealOnce` shows its section outright where there is no
  // IntersectionObserver; without the same fallback here the header would
  // appear and the rail under it would stay at `opacity: 0` forever. Read
  // through `useSyncExternalStore` so the server assumes an observer (matching
  // the hidden first paint) and only the client can decide otherwise.
  const observable = useSyncExternalStore(
    subscribeNever,
    () => typeof IntersectionObserver !== "undefined",
    () => true,
  );
  const revealed = inView || !observable;

  const reduced = useReducedMotion();

  const [panel, setPanel] = useState<PanelId>("crm");
  /** Which POS tab to return to when the reader comes back from CRM. */
  const [posPanel, setPosPanel] = useState<PosPanelId>("pos-monthly");
  /** Monthly or yearly, on the CRM tab. Kept across a trip to the POS tabs
      and back, so a reader comparing annual costs doesn't have to re-pick. */
  const [billing, setBilling] = useState<Billing>("monthly");
  const [direction, setDirection] = useState(1);
  const [height, setHeight] = useState<number>();

  const go = useCallback(
    (next: PanelId) => {
      if (next === panel) return;
      setDirection(PANELS.indexOf(next) > PANELS.indexOf(panel) ? 1 : -1);
      if (next !== "crm") setPosPanel(next);
      setPanel(next);
    },
    [panel],
  );

  // The navbar's mobile drawer links a tab by name: the anchor scrolls here,
  // this selects what it asked for. Guarded against an unknown id because the
  // payload comes off a window event, which anything can fire.
  useEffect(() => {
    const onSelect = (event: Event) => {
      const next = (event as CustomEvent<PanelId>).detail;
      if (PANELS.includes(next)) go(next);
    };
    window.addEventListener(PRICING_PANEL_EVENT, onSelect);
    return () => window.removeEventListener(PRICING_PANEL_EVENT, onSelect);
  }, [go]);

  // The element to measure is the panel Motion is currently bringing in, so it
  // arrives by callback ref rather than being reachable from a ref held here.
  const observer = useRef<ResizeObserver>(null);
  const measure = useCallback((node: HTMLDivElement | null) => {
    // The incoming panel mounts before the outgoing one leaves, so a `null`
    // here is always the old panel detaching after the new one has taken
    // over — disconnecting on it would tear down the live observer.
    if (!node) return;
    observer.current?.disconnect();
    setHeight(node.offsetHeight);
    // Panels reflow on resize and when the webfont lands; without this the
    // frame would keep whatever height it was first measured at and clip.
    const ro = new ResizeObserver(([entry]) =>
      setHeight(entry.borderBoxSize[0]?.blockSize ?? node.offsetHeight),
    );
    ro.observe(node);
    observer.current = ro;
  }, []);

  useEffect(() => () => observer.current?.disconnect(), []);

  const cardVariants = useMemo(
    () => makeCardVariants(Boolean(reduced)),
    [reduced],
  );
  const featureVariants = useMemo(
    () => makeFeatureVariants(Boolean(reduced)),
    [reduced],
  );

  const swap = { duration: reduced ? 0 : 0.45, ease: EASE };

  return (
    <section
      ref={reveal}
      id="pricing"
      aria-label={t("pricing.aria")}
      // #FCFCFC, the same ground every other section on the page stands on.
      // `scroll-mt-13` matches the fixed header, so the drawer's four pricing
      // links land on the heading rather than behind the bar.
      className="pr-reveal scroll-mt-13 bg-[#FCFCFC]"
    >
      <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center gap-4 px-4 py-12 md:px-[80px]">
        <header className="flex w-full flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-2 text-center">
            <h2 className="pr-heading text-[26px]/[38px] font-semibold text-black [text-wrap:balance] sm:text-[32px]/[44px] lg:text-[40px]/[48px]">
              {t("pricing.heading")}
            </h2>
            {/* Keyed on the panel so the line changes with the tab rather
                than swapping words mid-sentence. `wait`, like the panel below:
                two Thai sentences fading through each other is unreadable. */}
            <div className="pr-sub grid">
              <AnimatePresence initial={false} mode="wait">
                <m.p
                  key={panel}
                  initial={{ opacity: 0, y: reduced ? 0 : 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: reduced ? 0 : -6 }}
                  transition={{ duration: reduced ? 0 : 0.22, ease: EASE }}
                  className="[grid-area:1/1] text-[17px]/[26px] text-black/60 [text-wrap:balance] sm:text-[20px]/[28px]"
                >
                  {t(`pricing.subheads.${panel}`)}
                </m.p>
              </AnimatePresence>
            </div>
          </div>

          <div className="pr-switches flex w-full flex-col items-center">
            <Segmented
              label={t("pricing.switch.group")}
              tone="quiet"
              value={panel === "crm" ? "crm" : "pos"}
              onChange={(id) => go(id === "crm" ? "crm" : posPanel)}
              options={[
                { id: "crm", label: "CRM Package" },
                { id: "pos", label: "POS Packages" },
              ]}
            />

            {/* The billing cycle belongs to the CRM plans alone — the POS
                subscription is monthly only and the kits are bought once — so
                it opens and closes with that tab, on the same height animation
                the POS sub-tabs use below. */}
            <AnimatePresence initial={false}>
              {panel === "crm" ? (
                <m.div
                  key="crm-billing"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: reduced ? 0 : 0.35, ease: EASE }}
                  className="w-full overflow-hidden"
                >
                  <div className="flex flex-col items-center pt-6">
                    <Segmented
                      label={t("pricing.switch.billing")}
                      tone="brand"
                      value={billing}
                      onChange={setBilling}
                      options={[
                        { id: "monthly", label: t("pricing.switch.monthly") },
                        { id: "yearly", label: t("pricing.switch.yearly") },
                      ]}
                    />
                  </div>
                </m.div>
              ) : null}
            </AnimatePresence>

            {/* The sub-tabs exist only on the POS side, so they open and close
                on a height animation instead of snapping the header taller in
                a single frame. */}
            <AnimatePresence initial={false}>
              {panel !== "crm" ? (
                <m.div
                  key="pos-modes"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: reduced ? 0 : 0.35, ease: EASE }}
                  className="w-full overflow-hidden"
                >
                  <div className="flex flex-col items-center gap-4 pt-6">
                    <Segmented
                      label={t("pricing.switch.pos")}
                      tone="brand"
                      value={posPanel}
                      onChange={go}
                      options={[
                        {
                          id: "pos-monthly",
                          label: t("pricing.switch.posMonthly"),
                        },
                        {
                          id: "pos-kit",
                          label: t("pricing.switch.posKit"),
                        },
                      ]}
                    />
                    {/* One line from `lg`, where the container is finally
                        wide enough for the longer of the two captions; below
                        that it wraps and balances as before. */}
                    <p className="max-w-[640px] text-center text-[16px]/[24px] text-black/60 [text-wrap:balance] lg:max-w-none lg:whitespace-nowrap">
                      {t(`pricing.posCaptions.${posPanel}`)}
                    </p>
                  </div>
                </m.div>
              ) : null}
            </AnimatePresence>
          </div>
        </header>

        <m.div
          ref={stageRef}
          // `items-start` is load-bearing, not alignment taste. The panel below
          // is a grid item, and a grid item stretches to its row — which this
          // element sizes, from the very height being measured off that item.
          // Stretched, `measure` read the frame's *current* height back out of
          // the incoming panel instead of the panel's own, so `height` could
          // never come down: switching from the CRM rail to the shorter monthly
          // panel left the frame at the rail's height with the difference as
          // dead space above the FAQ — on a phone, where the rail stacks to
          // three full cards, some 600px of it. Sized to content instead, the
          // measurement is the panel's again and the frame follows it both ways.
          className="pr-stage grid w-full items-start overflow-hidden"
          initial={false}
          animate={{ height: height ?? "auto" }}
          transition={swap}
        >
          {/* `wait`, so the outgoing panel is gone before the incoming one
              arrives. Overlapping them means two sets of white cards and Thai
              copy printed over each other for a third of a second, which is
              why the frame holds its last measured height across the gap
              instead: nothing below moves while the stage is briefly empty. */}
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <m.div
              key={panel}
              ref={measure}
              custom={direction}
              variants={PANEL_VARIANTS}
              initial="enter"
              // Held at `enter` until the section is on screen, so the deal
              // isn't spent above the fold. After the first reveal `inView`
              // stays true and every tab switch plays straight away.
              animate={revealed ? "center" : "enter"}
              exit="exit"
              // `pb-7` is room for the panel's own drop shadow, not spacing.
              // This element is what the frame measures itself from, and the
              // frame clips (`overflow-hidden`, so a panel sliding in never
              // shows outside it) — so a box that ends exactly at the panel's
              // border edge cuts the shadow off flat along the bottom. 28px
              // clears the deepest one on either panel: the monthly card's
              // `0 4px 20.4px` reaches 24px down, and a hovered plan card's
              // `0 20px 40px -24px` about 22px past the row.
              className="w-full pb-7 [grid-area:1/1]"
            >
              {panel === "crm" ? (
                <>
                  <PlanRail
                    plans={crmPlans(t, billing)}
                    variants={cardVariants}
                    featureVariants={featureVariants}
                    armed={revealed}
                  />
                  {/* Inside the measured panel, not under the stage: the frame
                      takes its height from this element, and a line hung below
                      it would be clipped by the stage's `overflow-hidden`. */}
                  <p className="mx-auto mt-2 max-w-[760px] text-center text-[13px]/[20px] text-black/55 [text-wrap:balance]">
                    {t("pricing.crm.note")}
                  </p>
                </>
              ) : panel === "pos-monthly" ? (
                <MonthlyPanel variants={cardVariants} armed={revealed} />
              ) : (
                <PlanRail
                  plans={posKits(t)}
                  variants={cardVariants}
                  featureVariants={featureVariants}
                  armed={revealed}
                />
              )}
            </m.div>
          </AnimatePresence>
        </m.div>

        <PanelMirror panel={panel} />
      </div>
    </section>
  );
}
